<template>
  <!-- free hours from the psychologist's Google Calendar (secret iCal address), synced every 15 minutes -->
  <v-card flat outlined class="gcal pa-5 mb-6">
    <div class="d-flex align-center flex-wrap gcal__head">
      <v-icon color="primary" class="mr-2">mdi-calendar-sync</v-icon>
      <span class="gcal__title">{{ status.connected ? t("title") : t("howTitle") }}</span>
      <v-chip v-if="status.connected" small :color="status.error ? 'orange' : 'green'" text-color="white" class="ml-3">
        {{ status.error ? t("statusError") : t("statusOn") }}
      </v-chip>
    </div>

    <template v-if="!status.connected">
      <p class="mt-3 mb-2">{{ t("intro") }}</p>
      <!-- the steps are our own static texts with a link and bold words -->
      <p v-for="n in 4" :key="n" class="gcal__step" v-html="t('step' + n)"></p>
      <div class="d-flex flex-wrap align-start gcal__form">
        <v-text-field
          v-model="url"
          :label="t('urlLabel')"
          placeholder="https://calendar.google.com/calendar/ical/.../private-.../basic.ics"
          outlined
          dense
          hide-details="auto"
          :error-messages="error"
          class="gcal__input"
        ></v-text-field>
        <v-btn depressed color="primary" :loading="busy" :disabled="!url.trim()" @click="connect">{{ t("connect") }}</v-btn>
      </div>
      <p class="gcal__note mt-3 mb-0">{{ t("note") }}</p>
    </template>

    <template v-else>
      <p class="mt-3 mb-1">{{ t("connectedText") }}</p>
      <p class="mb-1">
        {{ t("syncedAt") }}: <b>{{ syncedAt }}</b>
        <template v-if="status.free_hours !== null"> · {{ t("freeHours") }}: <b>{{ status.free_hours }}</b></template>
      </p>
      <p v-if="status.error" class="orange--text text--darken-2 mb-1">{{ status.error }}</p>
      <div class="d-flex flex-wrap mt-3 gcal__actions">
        <v-btn small depressed color="primary" :loading="busy" @click="syncNow">
          <v-icon left small>mdi-refresh</v-icon>{{ t("syncNow") }}
        </v-btn>
        <v-btn small text color="red" :disabled="busy" @click="disconnect">{{ t("disconnect") }}</v-btn>
      </div>
    </template>
  </v-card>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";

const ENDPOINT = "api/psychologists/me/google-calendar";

export default {
  name: "GoogleCalendarSync",
  data() {
    return {
      status: { connected: false, synced_at: null, error: null, free_hours: null },
      url: "",
      error: null,
      busy: false,
    };
  },
  computed: {
    syncedAt() {
      if (!this.status.synced_at) return "—";
      return new Date(this.status.synced_at).toLocaleString("uk-UA", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  mounted() {
    ApiClient.get(ENDPOINT).then((response) => this.setStatus(response.data));
  },
  methods: {
    t(key) {
      return this.$vuetify.lang.t(`$vuetify.main.psychologistDashboard.googleCalendar.${key}`);
    },
    setStatus(payload, reload = false) {
      const wasConnected = this.status.connected;
      this.status = payload.data;
      this.$emit("change", this.status.connected);
      if (payload.message) this.toast(payload.message);
      // new free hours are shown in the schedule below
      if (reload || wasConnected !== this.status.connected) this.$emit("synced");
    },
    connect() {
      this.busy = true;
      this.error = null;
      // the 422 answer is shown under the field, not as a toast
      ApiClient.post(ENDPOINT, { url: this.url.trim() })
        .then((response) => {
          if (!response) return;
          this.url = "";
          this.setStatus(response.data, true);
        })
        .catch((error) => {
          this.error = (error && error.errors && error.errors.url && error.errors.url[0]) || this.t("failed");
        })
        .finally(() => {
          this.busy = false;
        });
    },
    syncNow() {
      this.busy = true;
      ApiClient.post(ENDPOINT + "/sync")
        .then((response) => this.setStatus(response.data, true))
        .finally(() => {
          this.busy = false;
        });
    },
    disconnect() {
      this.busy = true;
      ApiClient.delete(ENDPOINT)
        .then((response) => this.setStatus(response.data))
        .finally(() => {
          this.busy = false;
        });
    },
    toast(text) {
      Vue.swal({
        target: document.getElementById("main"),
        text,
        icon: "success",
        timer: 4000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.gcal {
  border-color: #d9e4eb !important;
  border-radius: 10px !important;
}
.gcal__head {
  row-gap: 6px;
}
.gcal__title {
  font-size: 18px;
  font-weight: 500;
}
.gcal__step {
  margin-bottom: 10px;
}
.gcal__step ::v-deep a {
  text-decoration: none;
}
.gcal__form {
  gap: 12px;
}
.gcal__input {
  flex: 1 1 320px;
}
.gcal__note {
  font-size: 13px;
  color: #8a8a8a;
}
.gcal__actions {
  gap: 8px;
}
</style>
